import { MutableRefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getElementSyncedMeasurements } from '../../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';
import { getConditionallySnappedSize } from '../resizeUtils';
import { BORDER_OFFSET_PX } from '../../../utils/grid/gridUtils';
import {
    getCardDimensionPropertiesPx,
    getEditorOverflowDistance,
    getIsAutoHeight,
    getIsEditorOverThreshold,
} from './resizableEditorUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';
import { EVENT_TYPE_NAMES } from '../../../../common/analytics/amplitudeEventTypesUtil';
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../../common/analytics/statsConstants';
import { CARD_MIN_WIDTH } from './ResizableEditorSaveHeightDecorator';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { ResizeAspectLock, SetElementSizeAndPosition, SetTempElementSize } from '../resizeTypes';
import { RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { DispatchUpdateElement } from '../../actions/elementActionTypes';
import { getDomElementId } from '../../utils/elementUtil';

// Determined via experimentation, not math
const AUTO_HEIGHT_MIN = 0;
const AUTO_HEIGHT_MAX = 20;

type OverflowChanges = {
    minHeight: number | null;
    maxHeight: number | null;
    autoHeight: boolean;
};

const getChangesForOverflowAmount = (
    overflowAmount: number,
    currentContainerHeight: number,
    isMultiResizingOrAnimating: boolean,
): OverflowChanges => {
    if (!isMultiResizingOrAnimating && overflowAmount <= AUTO_HEIGHT_MAX && overflowAmount >= AUTO_HEIGHT_MIN) {
        // container roughly matches the text - set auto height mode
        return {
            minHeight: null,
            maxHeight: null,
            autoHeight: true,
        };
    }

    if (overflowAmount > 0) {
        // text is overflowing (as the user intends) - set max height to
        // stop the container growing to fit it.
        return {
            minHeight: null,
            maxHeight: currentContainerHeight,
            autoHeight: false,
        };
    }

    // text has extra space - set min height so it doesn't collapse
    return {
        minHeight: currentContainerHeight,
        maxHeight: null,
        autoHeight: false,
    };
};

export type ResizableEditorHeightProps = {
    elementId: string;
    getCurrentMeasurements: (elementId: string) => Map<string, number>;
    getContextZoomScale: () => number;
    element: ImMNElement;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    setTempElementSize: SetTempElementSize;
    tempSize: RectSize | null;
    gridSize: number;
    isResizing: boolean;
    resizeAspectLock: ResizeAspectLock;
    minWidth: number;
    minHeight: number;
    allowAspectChangeForCurrentResize: boolean;
    isResizingOrAnimating: boolean;
    onResizeAnimationComplete: () => void;
    getNonMediaHeight: () => number;
    dispatchUpdateElement: DispatchUpdateElement;
    dispatchStopEditing: (elementId: string) => void;
    isEditing: boolean;
    inList?: boolean;
    isMultiResizingOrAnimating: boolean;
};

type HeightsState = {
    initialNonContainerHeight: number;
    editorHeight: number;
};

// If non container height is negligible at the start of the resize, we can assume there will be no change to account for.
// Only checking for a value over 1px to account for any minor discrepancies in measurements during animations.
const shouldIgnoreNonContainerHeight = (heightsStateRef: MutableRefObject<HeightsState>) =>
    heightsStateRef.current.initialNonContainerHeight < 1;

// oxlint-disable-next-line max-lines-per-function
const useResizableEditorHeightDecorator = (
    props: ResizableEditorHeightProps,
    getMinContainerHeight: (element: ImMNElement, gridSize: number, currentEditorHeight: number) => number,
    containerPaddingGu: number,
) => {
    const {
        element,
        setTempElementSize,
        isMultiResizingOrAnimating,
        gridSize,
        getContextZoomScale,
        dispatchUpdateElement,
        elementId,
        tempSize,
        isResizingOrAnimating,
        dispatchStopEditing,
        isEditing,
        inList,
        setElementSizeAndPosition,
    } = props;

    const heightsStateRef = useRef<HeightsState>({
        initialNonContainerHeight: 0,
        editorHeight: 0,
    });

    const containerRef = useRef<HTMLDivElement>();
    const setContainerRef = useCallback((node: HTMLDivElement) => {
        containerRef.current = node;
    }, []);

    const editorRef = useRef<HTMLDivElement>();
    const setEditorRef = useCallback((node: HTMLDivElement) => {
        editorRef.current = node;
    }, []);

    const elementDomNodeRef = useRef<HTMLElement | null>(null);
    const [tempContainerHeight, setTempContainerHeight] = useState<number | null>(null);
    const [initialised, setInitialised] = useState(false);

    const getCurrentContainerHeight = useCallback(() => {
        const zoomScale = getContextZoomScale();

        if (!containerRef.current) return 0;

        return containerRef?.current?.getBoundingClientRect().height / zoomScale;
    }, [getContextZoomScale]);

    const getCurrentElementHeight = useCallback((): number | null => {
        if (!elementDomNodeRef.current) {
            elementDomNodeRef.current = document.getElementById(getDomElementId(elementId));
        }
        const elementMeasurements = elementDomNodeRef.current?.getBoundingClientRect();
        if (!elementMeasurements) return getElementSyncedMeasurements(elementId).height;

        const zoomScale = getContextZoomScale();
        return elementMeasurements.height / zoomScale;
    }, [elementId, getContextZoomScale]);

    const getNonContainerHeight = useCallback((): number => {
        if (shouldIgnoreNonContainerHeight(heightsStateRef)) return 0;

        const currentElementHeight = getCurrentElementHeight();
        if (!currentElementHeight) return heightsStateRef.current.initialNonContainerHeight;

        return Math.max(currentElementHeight - getCurrentContainerHeight(), 0);
    }, [getCurrentContainerHeight, getCurrentElementHeight]);

    const getEditorHeight = useCallback(() => {
        if (!editorRef.current) return 0;

        const zoomScale = getContextZoomScale();

        const padding = containerPaddingGu * gridSize;

        return editorRef.current.getBoundingClientRect().height / zoomScale - padding;
    }, [containerPaddingGu, getContextZoomScale, gridSize]);

    const initialiseComponentHeights = useCallback(() => {
        setInitialised(true);

        // Use synced measurements singleton to always have the latest measurements
        const initialElementHeight = getElementSyncedMeasurements(elementId)?.height || 0;
        const initialContainerHeight = getCurrentContainerHeight();

        heightsStateRef.current = {
            initialNonContainerHeight: initialElementHeight - initialContainerHeight,
            editorHeight: getEditorHeight(),
        };
    }, [elementId, getCurrentContainerHeight, getEditorHeight]);

    const getNewContainerHeight = useCallback(
        ({ height }: RectSize, currentEditorHeight: number) => {
            // Container Height = Element Height - Non Container Height
            // Non Container Height = everything outside the resizable container
            // (e.g. reactions/labels) and can change height as width changes.
            const desiredElementHeightPx = height * gridSize;
            const nonContainerHeightPx = getNonContainerHeight();
            const minimumContainerHeight = getMinContainerHeight(element, gridSize, currentEditorHeight);
            return Math.max(desiredElementHeightPx - nonContainerHeightPx, minimumContainerHeight);
        },
        [element, getMinContainerHeight, getNonContainerHeight, gridSize],
    );

    const resetState = useCallback(() => {
        setTempElementSize(null);
        setTempContainerHeight(null);
        setInitialised(false);
    }, [setTempElementSize, setTempContainerHeight, setInitialised]);

    const setContainerSizeWhileResizing = useCallback(
        (size: RectSize | null) => {
            if (!size) {
                resetState();
                return;
            }

            if (!initialised) initialiseComponentHeights();

            const newHeight = getNewContainerHeight(size, heightsStateRef.current.editorHeight || 0);
            setTempContainerHeight(newHeight);

            setTempElementSize(getConditionallySnappedSize(size, isMultiResizingOrAnimating));
        },
        [
            getNewContainerHeight,
            initialised,
            initialiseComponentHeights,
            isMultiResizingOrAnimating,
            resetState,
            setTempElementSize,
        ],
    );

    useLayoutEffect(() => {
        // In most cases, there will be no content below the editor that needs to be accounted for, so we can skip this check
        if (shouldIgnoreNonContainerHeight(heightsStateRef)) return;

        if (!isResizingOrAnimating || !tempSize || !initialised) return;

        // Adjust container height to account for changes to footer wrapping (e.g. tags/reactions)
        const currentEditorHeight = heightsStateRef.current.editorHeight ?? getEditorHeight();
        const adjustedContainerHeight = getNewContainerHeight(tempSize, currentEditorHeight);

        setTempContainerHeight((previousHeight) => {
            if (previousHeight === null) return adjustedContainerHeight;

            const heightDif = Math.abs(previousHeight - adjustedContainerHeight);
            if (heightDif > 0) return adjustedContainerHeight;

            return previousHeight;
        });
    }, [getEditorHeight, getNewContainerHeight, tempSize, isResizingOrAnimating, initialised]);

    const setEditorElementSizeAndPosition = useCallback(
        (size: RectSize, position?: Point) => {
            if (!size && !position) return;

            if (!size) {
                setElementSizeAndPosition(null, position);
                return;
            }

            if (!initialised) initialiseComponentHeights();

            const newSize = getConditionallySnappedSize(size, isMultiResizingOrAnimating);

            newSize.height = Math.round(size.height) - BORDER_OFFSET_PX / gridSize;

            heightsStateRef.current.editorHeight = getEditorHeight();

            const containerHeightPx = getNewContainerHeight(newSize, heightsStateRef.current.editorHeight);
            const containerHeightGridUnits = containerHeightPx / gridSize;

            // Use the newly saved max height as the max height to compare against
            const overflowDistance = getEditorOverflowDistance(
                containerHeightPx,
                heightsStateRef.current.editorHeight,
                containerPaddingGu,
                gridSize,
            );

            const changes: OverflowChanges & {
                width?: number;
                height?: number;
            } = getChangesForOverflowAmount(overflowDistance, containerHeightGridUnits, isMultiResizingOrAnimating);

            // If the element is in a list, don't update its width
            if (!inList) {
                changes.width = Math.max(Math.round(newSize.width), CARD_MIN_WIDTH);
            }

            const elementId = getElementId(element);

            // Stop editing if resizing into a height limited size
            if (changes.maxHeight && isEditing) dispatchStopEditing(elementId);

            setElementSizeAndPosition(changes, position);

            sendAmplitudeEvent({
                eventType: EVENT_TYPE_NAMES.ELEMENT_UPDATE,
                userProperties: {
                    [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.RESIZE_CARD]: true },
                },
            });
        },
        [
            containerPaddingGu,
            dispatchStopEditing,
            element,
            getEditorHeight,
            getNewContainerHeight,
            gridSize,
            inList,
            initialiseComponentHeights,
            initialised,
            isEditing,
            isMultiResizingOrAnimating,
            setElementSizeAndPosition,
        ],
    );

    const handleDoubleClick = useCallback(() => {
        heightsStateRef.current.editorHeight = getEditorHeight();

        const { minHeight, maxHeight } = getCardDimensionPropertiesPx({ element, gridSize });
        const autoHeight = getIsAutoHeight(element);

        const canToggle =
            // If the editor height is less than the min height, then we may want to toggle the state of auto height
            (minHeight && heightsStateRef.current.editorHeight < minHeight) ||
            // If the editor height is greater than the max height, then we may want to toggle the auto height
            (maxHeight &&
                getIsEditorOverThreshold(
                    maxHeight,
                    heightsStateRef.current.editorHeight,
                    editorRef.current?.offsetTop || 0,
                    gridSize,
                ));

        if (!canToggle) return;

        return dispatchUpdateElement({
            id: getElementId(element),
            changes: {
                autoHeight: !autoHeight,
            },
        });
    }, [dispatchUpdateElement, element, getEditorHeight, gridSize]);

    return {
        cardEditorRef: setEditorRef,
        editorRef: setEditorRef,
        containerRef: setContainerRef,
        tempContainerHeight,
        handleDoubleClick,
        setElementSizeAndPosition: setEditorElementSizeAndPosition,
        setTempElementSize: setContainerSizeWhileResizing,
    };
};

export default useResizableEditorHeightDecorator;
